/**
 * Editor de modelo (modal xl): formulario a esquerda; a direita, em abas,
 * a previa ao vivo, a checagem das regras e o "antes e depois".
 *
 * Decisoes nao obvias:
 *  - O componente MONTA DO ZERO a cada abertura (o pai so o renderiza quando
 *    ha algo aberto), entao o estado inicial vem de `useState(() => ...)`, e
 *    nao de um efeito que sincroniza props: nao ha como o formulario "vazar"
 *    de um modelo para o outro.
 *  - Um so `EstadoEditor` para os quatro canais. Trocar o canal NAO apaga o
 *    que ja foi digitado; o que nao vale para o canal novo (botoes, rodape,
 *    assunto) simplesmente nao entra no modelo salvo, e a checagem avisa o
 *    que precisa mudar (ex.: `{{1}}` fora da API oficial).
 *  - "Fala com quem" e "Momento" so oferecem publicos e momentos DEPOIS DO
 *    FATO (T+ depois do abandono, D+ depois do vencimento). Nao existe opcao
 *    "antes de vencer" para escolher, e a checagem ainda procura lembrete
 *    preventivo escondido no texto.
 *  - Toda a validacao vem de `validacao.ts` como lista de regras: o checklist
 *    da aba "Checagem", os erros de cada campo e o bloqueio do "Salvar" leem a
 *    MESMA lista. Erros de formato aparecem enquanto digita; os de
 *    obrigatoriedade so depois da primeira tentativa de salvar.
 *  - O botao de resposta ganha um aviso explicito no proprio formulario, e a
 *    previa mostra o caminho de cada botao (DestinoDosBotoes): quem toca em
 *    "Tenho uma dúvida" cai com a vendedora e a regua para.
 *  - Nada aqui fala com a rede. "Salvar modelo (prévia)" devolve o
 *    `MessageTemplate` montado (com a versao nova no historico) e o pai decide
 *    onde guarda.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { Badge, Button, Checkbox, Field, FilterChips, Icon, Input, Modal, Panel, Select, Tabs, Textarea, type ChipOption, type TabItem } from '../../../components/ui';
import { formatDate } from '../../../lib/format';
import type { CompanyId, CompanySummary } from '../../../types';
import type { ButtonKind, MessageTemplate, TemplateAudience, TemplateButton, TemplateCategory, TemplateChannel, TemplateSector, VoiceScript } from '../types';
import { checarModelo, contarFalhas, errosPorCampo, maiorPosicao, type EntradaChecagem } from '../validacao';
import { frasearMudancas, proximaVersao, resumirMudancas, versaoDe } from '../versoes';
import {
  BOTAO_KIND_LABEL,
  CANAIS,
  CANAL_LABEL,
  CATEGORIAS,
  CATEGORIA_DICA,
  CATEGORIA_LABEL,
  LIMITE_BOTOES,
  LIMITE_CORPO_WHATSAPP,
  LIMITE_PRE_CABECALHO,
  LIMITE_RODAPE,
  LIMITE_ROTULO_BOTAO,
  MOMENTOS,
  PUBLICOS,
  PUBLICO_DESCRICAO,
  PUBLICO_LABEL,
  SETORES,
  SETOR_LABEL,
  VARIAVEIS,
  canalPassaPelaMeta,
  canalTemBotoes,
  canalTemRodape,
  extrairVariaveis,
} from '../vocabulario';
import { ChecagemMeta } from './ChecagemMeta';
import { DestinoDosBotoes } from './DestinoDosBotoes';
import { PreviaDoCanal } from './PreviaDoCanal';

export type ModoEditor = 'novo' | 'editar';

export interface EditorAberto {
  readonly modo: ModoEditor;
  /** Modelo de origem ao editar; `null` ao criar. */
  readonly base: MessageTemplate | null;
  /** Nome do original quando o modelo em edicao acabou de ser duplicado. */
  readonly copiaDe?: string;
}

interface BotaoEditor {
  readonly kind: ButtonKind;
  readonly label: string;
  readonly url: string;
}

interface EstadoEditor {
  readonly channel: TemplateChannel;
  readonly category: TemplateCategory;
  readonly sector: TemplateSector;
  readonly audience: TemplateAudience;
  /** '' = disparo avulso (sem toque fixo na regua). */
  readonly touch: string;
  readonly companyId: CompanyId | null;
  readonly name: string;
  readonly body: string;
  readonly footer: string;
  readonly subject: string;
  readonly preheader: string;
  readonly emailCta: string;
  readonly emailUrl: string;
  readonly buttons: readonly BotaoEditor[];
  readonly voice: VoiceScript;
  readonly variableMap: readonly string[];
  readonly sendToMeta: boolean;
}

type BlocoVoz = keyof VoiceScript;
type QuantidadeBotoes = '0' | '1' | '2';
type AbaLateral = 'previa' | 'checagem' | 'antes_depois';

const VALOR_GLOBAL = 'global';
const VALOR_AVULSO = '';
const URL_PADRAO = '{link}';
const PLACEHOLDER_CORPO = 'Seu texto aparece aqui conforme você escreve…';
const VOZ_VAZIA: VoiceScript = { opening: '', objection: '', closing: '' };

const BLOCOS_VOZ: readonly { readonly chave: BlocoVoz; readonly rotulo: string; readonly dica: string; readonly linhas: number }[] = [
  { chave: 'opening', rotulo: 'Abertura', dica: 'Quem fala, por que ligou (o checkout que ficou parado, o acesso que venceu) e a pergunta que abre a conversa.', linhas: 4 },
  { chave: 'objection', rotulo: 'Se o lead objetar', dica: 'Uma linha por objeção: o que a IA pode responder e o que não pode prometer (nada de cupom, nada de insistir).', linhas: 5 },
  { chave: 'closing', rotulo: 'Fechamento', dica: 'Combina o próximo passo (link no WhatsApp) e encerra.', linhas: 3 },
];

// -----------------------------------------------------------------------------
// Estado
// -----------------------------------------------------------------------------

function estadoInicial(base: MessageTemplate | null, empresaPadrao: CompanyId | null): EstadoEditor {
  if (base === null) {
    return {
      channel: 'whatsapp_oficial',
      category: 'utility',
      sector: 'funil',
      audience: 'checkout_abandonado',
      touch: 'T+1h',
      companyId: empresaPadrao,
      name: '',
      body: '',
      footer: '',
      subject: '',
      preheader: '',
      emailCta: '',
      emailUrl: URL_PADRAO,
      buttons: [],
      voice: VOZ_VAZIA,
      variableMap: [],
      sendToMeta: true,
    };
  }
  const cta = base.channel === 'email' ? base.buttons[0] : undefined;
  return {
    channel: base.channel,
    category: base.category,
    sector: base.sector,
    audience: base.audience,
    touch: base.touch ?? VALOR_AVULSO,
    companyId: base.companyId,
    name: base.name,
    body: base.body,
    footer: base.footer ?? '',
    subject: base.subject ?? '',
    preheader: base.preheader ?? '',
    emailCta: cta?.label ?? '',
    emailUrl: cta?.url ?? URL_PADRAO,
    buttons: base.channel === 'whatsapp_oficial' ? base.buttons.map((b) => ({ kind: b.kind, label: b.label, url: b.url ?? URL_PADRAO })) : [],
    voice: base.voice ?? { ...VOZ_VAZIA, opening: base.body },
    variableMap: base.variableMap,
    sendToMeta: true,
  };
}

function comBloco(voice: VoiceScript, bloco: BlocoVoz, texto: string): VoiceScript {
  switch (bloco) {
    case 'opening':
      return { ...voice, opening: texto };
    case 'objection':
      return { ...voice, objection: texto };
    case 'closing':
      return { ...voice, closing: texto };
  }
}

function entradaDe(estado: EstadoEditor, nomesExistentes: readonly string[]): EntradaChecagem {
  return {
    channel: estado.channel,
    category: estado.category,
    sector: estado.sector,
    audience: estado.audience,
    name: estado.name,
    nomesExistentes,
    body: estado.body,
    footer: estado.footer,
    subject: estado.subject,
    preheader: estado.preheader,
    buttons: estado.buttons,
    emailCta: estado.emailCta,
    emailUrl: estado.emailUrl,
    voice: estado.voice,
    variableMap: estado.variableMap,
  };
}

function notaDeStatus(estado: EstadoEditor, base: MessageTemplate | null, agoraIso: string): string | null {
  if (!canalPassaPelaMeta(estado.channel)) {
    return null;
  }
  const data = formatDate(agoraIso);
  if (base !== null && base.channel === 'whatsapp_oficial' && base.status === 'aprovado') {
    return estado.sendToMeta
      ? `Editado em ${data}: toda edição exige nova aprovação da Meta. Reenviado (prévia).`
      : `Editado em ${data}: toda edição exige nova aprovação da Meta. Ainda não reenviado.`;
  }
  return estado.sendToMeta ? `Enviado à Meta em ${data} (prévia). A análise costuma levar até 24 h.` : 'Salvo sem enviar à Meta: fica pendente até o envio.';
}

export function montarTemplate(estado: EstadoEditor, base: MessageTemplate | null, id: string, agoraIso: string): MessageTemplate {
  const buttons: readonly TemplateButton[] =
    estado.channel === 'whatsapp_oficial'
      ? estado.buttons.map((b) => (b.kind === 'link' ? { kind: b.kind, label: b.label.trim(), url: b.url.trim() } : { kind: b.kind, label: b.label.trim() }))
      : estado.channel === 'email' && estado.emailCta.trim() !== ''
        ? [{ kind: 'link', label: estado.emailCta.trim(), url: estado.emailUrl.trim() }]
        : [];
  const voice = estado.channel === 'voice' ? estado.voice : null;
  const body = estado.channel === 'voice' ? estado.voice.opening : estado.body;
  const footer = canalTemRodape(estado.channel) && estado.footer.trim() !== '' ? estado.footer.trim() : null;
  const email: { subject?: string; preheader?: string } = {};
  if (estado.channel === 'email') {
    email.subject = estado.subject.trim();
    if (estado.preheader.trim() !== '') {
      email.preheader = estado.preheader.trim();
    }
  }
  const posicoes = canalPassaPelaMeta(estado.channel) ? maiorPosicao(body) : 0;
  const variableMap = Array.from({ length: posicoes }, (_, i) => estado.variableMap[i] ?? '');
  const nomeadas = extrairVariaveis(body, email.subject, email.preheader, footer, voice?.objection, voice?.closing, ...buttons.map((b) => b.url));
  const variables = posicoes > 0 ? Array.from(new Set([...variableMap.filter((n) => n !== ''), ...nomeadas])) : nomeadas;

  const rascunho: MessageTemplate = {
    id,
    name: estado.name,
    channel: estado.channel,
    category: estado.category,
    sector: estado.sector,
    audience: estado.audience,
    touch: estado.touch === VALOR_AVULSO ? null : estado.touch,
    status: canalPassaPelaMeta(estado.channel) ? 'pendente' : 'texto_livre',
    statusNote: notaDeStatus(estado, base, agoraIso),
    body,
    footer,
    ...email,
    variables,
    variableMap,
    buttons,
    voice,
    companyId: estado.companyId,
    usedIn: base?.usedIn ?? [],
    performance: base?.performance ?? { sent: 0, delivered: 0, opened: null, clicked: null, replied: 0, paid: 0, paidViaSeller: 0, recoveredCents: 0 },
    previousPerformance: base?.previousPerformance ?? null,
    versions: base?.versions ?? [],
    updatedAt: agoraIso,
  };

  if (base === null) {
    return { ...rascunho, versions: [versaoDe(rascunho, 1, agoraIso, 'Criado na prévia')] };
  }
  const mudancas = resumirMudancas(base, rascunho);
  if (mudancas.length === 0) {
    // Nada mudou: nao rebaixa um aprovado para pendente nem cria versao vazia.
    return base;
  }
  // Publico e empresa sao nossos (a Meta nao ve). Na API oficial, nome e categoria fazem parte do
  // modelo aprovado: mudar qualquer um deles volta para a analise. Fora da Meta, nada exige aprovacao.
  const livres = canalPassaPelaMeta(rascunho.channel) ? ['público', 'empresa'] : ['público', 'empresa', 'nome', 'categoria'];
  const soMetadado = base.channel === rascunho.channel && mudancas.every((m) => livres.includes(m));
  const comStatus: MessageTemplate = soMetadado ? { ...rascunho, status: base.status, statusNote: base.statusNote } : rascunho;
  return { ...comStatus, versions: [...base.versions, versaoDe(comStatus, proximaVersao(base), agoraIso, frasearMudancas(mudancas))] };
}

function inserirNoTexto(atual: string, token: string, el: HTMLTextAreaElement | null): { readonly texto: string; readonly caret: number } {
  const inicio = el?.selectionStart ?? atual.length;
  const fim = el?.selectionEnd ?? inicio;
  return { texto: `${atual.slice(0, inicio)}${token}${atual.slice(fim)}`, caret: inicio + token.length };
}

function restaurarCaret(el: HTMLTextAreaElement | null, caret: number): void {
  if (el === null) {
    return;
  }
  window.requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(caret, caret);
  });
}

function parseDe<T extends string>(lista: readonly T[], valor: string): T | null {
  return lista.find((v) => v === valor) ?? null;
}

// -----------------------------------------------------------------------------
// Componente
// -----------------------------------------------------------------------------

export function ModalEditorModelo({
  aberto,
  companies,
  empresaPadrao,
  nomesExistentes,
  gerarId,
  onCancelar,
  onSalvar,
}: {
  readonly aberto: EditorAberto | null;
  readonly companies: readonly CompanySummary[];
  /** Empresa pre-selecionada ao criar (a do filtro da tela), ou `null` = global. */
  readonly empresaPadrao: CompanyId | null;
  /** Nomes ja usados na biblioteca (o do proprio modelo em edicao sai na comparacao). */
  readonly nomesExistentes: readonly string[];
  readonly gerarId: () => string;
  /** PRECISA ter identidade estavel (useCallback): o Modal o poe nas dependencias do efeito de foco. */
  readonly onCancelar: () => void;
  readonly onSalvar: (template: MessageTemplate, modo: ModoEditor, mudou: boolean) => void;
}): JSX.Element | null {
  if (aberto === null) {
    return null;
  }
  return (
    <EditorDeModelo
      aberto={aberto}
      companies={companies}
      empresaPadrao={empresaPadrao}
      nomesExistentes={nomesExistentes}
      gerarId={gerarId}
      onCancelar={onCancelar}
      onSalvar={onSalvar}
    />
  );
}

function EditorDeModelo({
  aberto,
  companies,
  empresaPadrao,
  nomesExistentes,
  gerarId,
  onCancelar,
  onSalvar,
}: {
  readonly aberto: EditorAberto;
  readonly companies: readonly CompanySummary[];
  readonly empresaPadrao: CompanyId | null;
  readonly nomesExistentes: readonly string[];
  readonly gerarId: () => string;
  readonly onCancelar: () => void;
  readonly onSalvar: (template: MessageTemplate, modo: ModoEditor, mudou: boolean) => void;
}): JSX.Element {
  const { modo, base, copiaDe } = aberto;
  const [estado, setEstado] = useState<EstadoEditor>(() => estadoInicial(base, empresaPadrao));
  const [tentou, setTentou] = useState(false);
  const [aba, setAba] = useState<AbaLateral>('previa');
  const [blocoAtivo, setBlocoAtivo] = useState<BlocoVoz>('opening');
  const agoraRef = useRef(new Date().toISOString());
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const openingRef = useRef<HTMLTextAreaElement | null>(null);
  const objectionRef = useRef<HTMLTextAreaElement | null>(null);
  const closingRef = useRef<HTMLTextAreaElement | null>(null);

  const patch = useCallback((parcial: Partial<EstadoEditor>): void => {
    setEstado((anterior) => ({ ...anterior, ...parcial }));
  }, []);

  const nomesParaComparar = useMemo(() => (base === null ? nomesExistentes : nomesExistentes.filter((n) => n !== base.name)), [nomesExistentes, base]);
  const regras = useMemo(() => checarModelo(entradaDe(estado, nomesParaComparar)), [estado, nomesParaComparar]);
  const falhas = contarFalhas(regras);
  const valido = falhas.erros === 0;
  const erros = errosPorCampo(regras, tentou);
  const erroDe = (campo: string): string | undefined => erros[campo];

  const empresa = companies.find((c) => c.id === estado.companyId) ?? null;
  const meta = canalPassaPelaMeta(estado.channel);
  const ehWhatsApp = estado.channel === 'whatsapp_oficial' || estado.channel === 'whatsapp_evolution';
  const posicoes = meta ? maiorPosicao(estado.body) : 0;

  // Previa ao vivo: corpo vazio ganha um texto de espera e botao sem rotulo ganha um nome provisorio.
  const previa = useMemo(() => {
    const paraPrevia: EstadoEditor = {
      ...estado,
      body: estado.body === '' ? PLACEHOLDER_CORPO : estado.body,
      buttons: estado.buttons.map((b, i) => ({ ...b, label: b.label.trim() === '' ? `Botão ${i + 1}` : b.label })),
    };
    return montarTemplate(paraPrevia, null, base?.id ?? 'SIMULADO-modelo-previa', agoraRef.current);
  }, [estado, base]);

  const mudancas = useMemo(() => (base === null ? [] : resumirMudancas(base, montarTemplate(estado, null, base.id, agoraRef.current))), [estado, base]);

  const refDoBloco = (bloco: BlocoVoz): React.MutableRefObject<HTMLTextAreaElement | null> => {
    switch (bloco) {
      case 'opening':
        return openingRef;
      case 'objection':
        return objectionRef;
      case 'closing':
        return closingRef;
    }
  };

  const inserir = (token: string): void => {
    if (estado.channel === 'voice') {
      const el = refDoBloco(blocoAtivo).current;
      const { texto, caret } = inserirNoTexto(estado.voice[blocoAtivo], token, el);
      patch({ voice: comBloco(estado.voice, blocoAtivo, texto) });
      restaurarCaret(el, caret);
      return;
    }
    const el = bodyRef.current;
    const { texto, caret } = inserirNoTexto(estado.body, token, el);
    patch({ body: texto });
    restaurarCaret(el, caret);
  };

  const definirMapa = (indice: number, nome: string): void => {
    const proximo = Array.from({ length: Math.max(posicoes, estado.variableMap.length, indice + 1) }, (_, i) => (i === indice ? nome : estado.variableMap[i] ?? ''));
    patch({ variableMap: proximo });
  };

  const quantidadeBotoes: QuantidadeBotoes = estado.buttons.length >= LIMITE_BOTOES ? '2' : estado.buttons.length === 1 ? '1' : '0';
  const definirQuantidade = (valor: QuantidadeBotoes): void => {
    const alvo = Number(valor);
    const atuais = estado.buttons.slice(0, alvo);
    while (atuais.length < alvo) {
      atuais.push(atuais.length === 0 ? { kind: 'link', label: '', url: URL_PADRAO } : { kind: 'reply', label: 'Tenho uma dúvida', url: URL_PADRAO });
    }
    patch({ buttons: atuais });
  };
  const alterarBotao = (indice: number, parcial: Partial<BotaoEditor>): void => {
    patch({ buttons: estado.buttons.map((b, i) => (i === indice ? { ...b, ...parcial } : b)) });
  };

  const salvar = (): void => {
    setTentou(true);
    if (!valido) {
      setAba('checagem');
      return;
    }
    const montado = montarTemplate(estado, base, base?.id ?? gerarId(), new Date().toISOString());
    onSalvar(montado, modo, base === null || montado !== base);
  };

  const opcoesBotoes: readonly ChipOption<QuantidadeBotoes>[] = [
    { value: '0', label: 'Sem botão' },
    { value: '1', label: '1 botão' },
    { value: '2', label: '2 botões' },
  ];

  const abas: readonly TabItem<AbaLateral>[] = [
    { value: 'previa', label: 'Prévia ao vivo', icon: 'olho' },
    { value: 'checagem', label: 'Checagem', icon: 'escudo', count: falhas.erros + falhas.avisos === 0 ? 'ok' : String(falhas.erros + falhas.avisos) },
    ...(base === null ? [] : [{ value: 'antes_depois' as const, label: 'Antes e depois', icon: 'copiar' as const }]),
  ];

  const variaveisVisiveis = VARIAVEIS.filter((v) => v.name !== 'vendedora' || estado.audience === 'respondeu_regua');
  const momentos = MOMENTOS[estado.audience];

  return (
    <Modal
      open
      onClose={onCancelar}
      size="xl"
      title={modo === 'novo' ? 'Novo modelo' : copiaDe !== undefined ? 'Ajustar a cópia' : 'Editar modelo'}
      description={
        copiaDe !== undefined
          ? `Cópia de ${copiaDe}, já salva na prévia. Ajuste o que quiser e salve; a checagem acompanha cada mudança.`
          : 'Escolha com quem o modelo fala, escreva o texto e acompanhe a prévia e a checagem ao vivo. Nada sai daqui: é a prévia.'
      }
      footer={
        <>
          <span className="mr-auto text-xs" role="status">
            {falhas.erros > 0 ? (
              <span className="text-status-critical">{falhas.erros === 1 ? '1 erro impede salvar' : `${falhas.erros} erros impedem salvar`}</span>
            ) : falhas.avisos > 0 ? (
              <span className="text-status-warning">{falhas.avisos === 1 ? '1 aviso — pode salvar' : `${falhas.avisos} avisos — pode salvar`}</span>
            ) : (
              <span className="text-status-good">{meta ? 'Pronto para enviar à Meta' : 'Pronto para salvar'}</span>
            )}
          </span>
          <Button variant="ghost" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" onClick={salvar}>
            Salvar modelo (prévia)
          </Button>
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <form
          className="flex flex-col gap-5"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            salvar();
          }}
        >
          {tentou && !valido ? (
            <p role="alert" className="flex flex-wrap items-center gap-2 rounded-xl border border-status-critical/40 bg-status-critical/10 px-4 py-3 text-sm text-ink">
              Revise os campos destacados antes de salvar.
              <Button variant="ghost" size="sm" onClick={() => setAba('checagem')}>
                Ver a checagem
              </Button>
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Canal" required>
              {({ id }) => (
                <Select
                  id={id}
                  value={estado.channel}
                  onChange={(e) => {
                    const canal = parseDe(CANAIS, e.target.value);
                    if (canal !== null) {
                      patch({ channel: canal });
                    }
                  }}
                >
                  {CANAIS.map((c) => (
                    <option key={c} value={c}>
                      {CANAL_LABEL[c]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Categoria" required hint={CATEGORIA_DICA[estado.category]}>
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={estado.category}
                  onChange={(e) => {
                    const categoria = parseDe(CATEGORIAS, e.target.value);
                    if (categoria !== null) {
                      patch({ category: categoria });
                    }
                  }}
                >
                  {CATEGORIAS.map((c) => (
                    <option key={c} value={c}>
                      {CATEGORIA_LABEL[c]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Setor" required>
              {({ id }) => (
                <Select
                  id={id}
                  value={estado.sector}
                  onChange={(e) => {
                    const setor = parseDe(SETORES, e.target.value);
                    if (setor !== null) {
                      patch({ sector: setor });
                    }
                  }}
                >
                  {SETORES.map((s) => (
                    <option key={s} value={s}>
                      {SETOR_LABEL[s]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Empresa" required hint="Global vale para todas; de uma empresa substitui o global só nela.">
              {({ id, describedBy }) => (
                <Select id={id} aria-describedby={describedBy} value={estado.companyId ?? VALOR_GLOBAL} onChange={(e) => patch({ companyId: e.target.value === VALOR_GLOBAL ? null : e.target.value })}>
                  <option value={VALOR_GLOBAL}>Global (todas as empresas)</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>

          <fieldset className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-4">
            <legend className="px-1 text-sm font-semibold text-ink">Fala com quem (sempre depois do fato)</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Público" required hint={PUBLICO_DESCRICAO[estado.audience]}>
                {({ id, describedBy }) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={estado.audience}
                    onChange={(e) => {
                      const publico = parseDe(PUBLICOS, e.target.value);
                      if (publico !== null) {
                        const lista = MOMENTOS[publico];
                        patch({ audience: publico, touch: lista.includes(estado.touch) || estado.touch === VALOR_AVULSO ? estado.touch : lista[0] ?? VALOR_AVULSO });
                      }
                    }}
                  >
                    {PUBLICOS.map((p) => (
                      <option key={p} value={p}>
                        {PUBLICO_LABEL[p]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Momento" hint="T+ conta depois do abandono; D+ depois do vencimento.">
                {({ id, describedBy }) => (
                  <Select id={id} aria-describedby={describedBy} value={estado.touch} onChange={(e) => patch({ touch: e.target.value })}>
                    {momentos.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                    <option value={VALOR_AVULSO}>Disparo avulso (sem toque fixo)</option>
                  </Select>
                )}
              </Field>
            </div>
            <p className="flex items-start gap-2 text-xs text-ink-subtle">
              <Icon name="escudo" size="xs" className="mt-0.5 shrink-0" />
              Não existe opção “antes de vencer”: o Converte Fácil só fala com quem já abandonou o checkout (30 min sem pagar) ou já teve o plano vencido. Sem o fato não há
              recuperação nem comissão — e a checagem procura lembrete preventivo escondido no texto.
            </p>
          </fieldset>

          <Field label="Nome do modelo" required hint="Minúsculas, números e underscore. Ex.: carrinho_t1h_2botoes" error={erroDe('name')}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={estado.name}
                onChange={(e) => patch({ name: e.target.value.toLowerCase() })}
                placeholder="carrinho_t1h_2botoes"
                autoComplete="off"
                spellCheck={false}
                className="font-mono"
              />
            )}
          </Field>

          {estado.channel === 'email' ? (
            <div className="grid gap-4">
              <Field label="Assunto" required error={erroDe('subject')}>
                {({ id, describedBy, invalid }) => (
                  <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} value={estado.subject} onChange={(e) => patch({ subject: e.target.value })} placeholder="{primeiro_nome}, seu {produto} ficou parado no checkout" />
                )}
              </Field>
              <Field label="Pré-cabeçalho" hint={<Contador atual={estado.preheader.length} limite={LIMITE_PRE_CABECALHO} texto="A linha que aparece depois do assunto na caixa de entrada." />} error={erroDe('preheader')}>
                {({ id, describedBy }) => <Input id={id} aria-describedby={describedBy} value={estado.preheader} onChange={(e) => patch({ preheader: e.target.value })} placeholder="Dá pra terminar em menos de um minuto." />}
              </Field>
            </div>
          ) : null}

          <div role="group" aria-label="Inserir variável" className="flex flex-col gap-2">
            <span className="text-xs font-medium text-ink-subtle">
              {estado.channel === 'voice' ? `Inserir variável em “${BLOCOS_VOZ.find((b) => b.chave === blocoAtivo)?.rotulo ?? 'Abertura'}”:` : 'Inserir variável no texto:'}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              {variaveisVisiveis.map((v) => (
                <Button key={v.name} type="button" variant="secondary" size="sm" onClick={() => inserir(`{${v.name}}`)} title={`${v.label} — ex.: ${v.exemplo}`}>
                  <span className="font-mono">{`{${v.name}}`}</span>
                </Button>
              ))}
              {meta ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => inserir(`{{${posicoes + 1}}}`)} title="Variável numerada, no estilo da Meta. Depois escolha o que o motor coloca nela.">
                  <span className="font-mono">{`{{${posicoes + 1}}}`}</span> numerada
                </Button>
              ) : null}
            </div>
          </div>

          {estado.channel === 'voice' ? (
            <div className="flex flex-col gap-4">
              {BLOCOS_VOZ.map((bloco) => (
                <Field key={bloco.chave} label={bloco.rotulo} required={bloco.chave !== 'objection'} hint={bloco.dica} error={erroDe(bloco.chave)}>
                  {({ id, describedBy, invalid }) => (
                    <Textarea
                      id={id}
                      aria-describedby={describedBy}
                      aria-invalid={invalid}
                      rows={bloco.linhas}
                      value={estado.voice[bloco.chave]}
                      onFocus={(e) => {
                        // O kit nao repassa `ref`; o elemento e capturado no foco para a insercao de variavel achar o cursor.
                        refDoBloco(bloco.chave).current = e.currentTarget;
                        setBlocoAtivo(bloco.chave);
                      }}
                      onChange={(e) => patch({ voice: comBloco(estado.voice, bloco.chave, e.target.value) })}
                    />
                  )}
                </Field>
              ))}
            </div>
          ) : (
            <Field
              label={estado.channel === 'email' ? 'Texto do e-mail' : 'Texto da mensagem'}
              required
              hint={ehWhatsApp ? <Contador atual={estado.body.length} limite={LIMITE_CORPO_WHATSAPP} texto="Um Enter separa parágrafos." /> : 'Um Enter separa parágrafos. O botão de ação entra no fim.'}
              error={erroDe('body')}
            >
              {({ id, describedBy, invalid }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  rows={ehWhatsApp ? 6 : 8}
                  value={estado.body}
                  onFocus={(e) => {
                    bodyRef.current = e.currentTarget;
                  }}
                  onChange={(e) => patch({ body: e.target.value })}
                  placeholder={estado.audience === 'plano_vencido' ? 'Oi, {primeiro_nome}! Seu acesso ao {plano} venceu há 15 dias…' : 'Oi, {primeiro_nome}! Vi que o seu {produto} ficou parado no checkout…'}
                />
              )}
            </Field>
          )}

          {posicoes > 0 ? (
            <fieldset className="flex flex-col gap-3 rounded-2xl border border-line bg-surface p-4">
              <legend className="px-1 text-sm font-semibold text-ink">O que o motor coloca em cada variável numerada</legend>
              {erroDe('variableMap') === undefined ? null : (
                <p role="alert" className="text-xs text-status-critical">
                  {erroDe('variableMap')}
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {Array.from({ length: posicoes }, (_, i) => (
                  <Field key={i} label={<span className="font-mono">{`{{${i + 1}}}`}</span>}>
                    {({ id }) => (
                      <Select id={id} size="sm" value={estado.variableMap[i] ?? ''} onChange={(e) => definirMapa(i, e.target.value)}>
                        <option value="">Escolha o campo…</option>
                        {variaveisVisiveis.map((v) => (
                          <option key={v.name} value={v.name}>
                            {v.label} (ex.: {v.exemplo})
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                ))}
              </div>
            </fieldset>
          ) : null}

          {canalTemRodape(estado.channel) ? (
            <Field label="Rodapé (opcional)" hint={<Contador atual={estado.footer.length} limite={LIMITE_RODAPE} texto={meta ? 'Linha pequena, em cinza. Sem variável.' : 'Linha pequena abaixo do texto.'} />} error={erroDe('footer')}>
              {({ id, describedBy, invalid }) => (
                <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} value={estado.footer} onChange={(e) => patch({ footer: e.target.value })} placeholder="Responda SAIR para não receber mais lembretes." />
              )}
            </Field>
          ) : null}

          {canalTemBotoes(estado.channel) ? (
            <fieldset className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-4">
              <legend className="px-1 text-sm font-semibold text-ink">Botões</legend>
              <FilterChips label="Quantidade de botões" prefix="Botões:" options={opcoesBotoes} value={quantidadeBotoes} onChange={definirQuantidade} />
              {estado.buttons.map((botao, i) => (
                <div key={i} className="grid gap-3 rounded-xl border border-line bg-elevated p-3 sm:grid-cols-2">
                  <Field label={`Botão ${i + 1} · tipo`}>
                    {({ id }) => (
                      <Select id={id} size="sm" value={botao.kind} onChange={(e) => alterarBotao(i, { kind: e.target.value === 'reply' ? 'reply' : 'link' })}>
                        <option value="link">{BOTAO_KIND_LABEL.link}</option>
                        <option value="reply">{BOTAO_KIND_LABEL.reply}</option>
                      </Select>
                    )}
                  </Field>
                  <Field label="Texto do botão" required hint={<Contador atual={botao.label.length} limite={LIMITE_ROTULO_BOTAO} />} error={erroDe(`button-${i}-label`)}>
                    {({ id, describedBy, invalid }) => (
                      <Input
                        id={id}
                        size="sm"
                        aria-describedby={describedBy}
                        aria-invalid={invalid}
                        value={botao.label}
                        onChange={(e) => alterarBotao(i, { label: e.target.value })}
                        placeholder={botao.kind === 'link' ? 'Finalizar compra' : 'Tenho uma dúvida'}
                      />
                    )}
                  </Field>
                  {botao.kind === 'link' ? (
                    <Field label="Link" required hint="Use {link}: o checkout do próprio lead, com a marca da régua que mede o clique." error={erroDe(`button-${i}-url`)} className="sm:col-span-2">
                      {({ id, describedBy, invalid }) => (
                        <Input id={id} size="sm" aria-describedby={describedBy} aria-invalid={invalid} value={botao.url} onChange={(e) => alterarBotao(i, { url: e.target.value })} placeholder="{link}" className="font-mono" />
                      )}
                    </Field>
                  ) : (
                    <div className="flex items-start gap-2.5 rounded-xl border border-series-7/40 bg-series-7/5 px-3 py-2.5 sm:col-span-2">
                      <Icon name="resposta" size="sm" className="mt-0.5 shrink-0 text-series-7" />
                      {estado.audience === 'respondeu_regua' ? (
                        <p className="text-xs text-ink-muted">
                          <span className="font-semibold text-ink">Quem tocar aqui volta para a conversa com a vendedora do comercial.</span> A régua já está parada para esse
                          lead desde a primeira resposta. Este modelo não gera crédito próprio: a comissão fica com o toque da régua que trouxe o lead (ADR-001 E5).
                        </p>
                      ) : estado.audience === 'pagou_apos_toque' ? (
                        <p className="text-xs text-ink-muted">
                          <span className="font-semibold text-ink">Quem tocar aqui cai no atendimento da empresa.</span> A régua já parou com o pagamento, e este modelo não gera
                          crédito: a venda já foi atribuída ao toque anterior.
                        </p>
                      ) : (
                        <p className="text-xs text-ink-muted">
                          <span className="font-semibold text-ink">Quem tocar aqui cai com a vendedora do comercial e a régua para na hora</span> para esse lead — nenhum toque
                          seguinte é enviado enquanto ela atende. Se a vendedora fechar em até 7 dias, a comissão continua creditada a este toque; ela aparece como “fechado por”.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </fieldset>
          ) : null}

          {estado.channel === 'email' ? (
            <fieldset className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-4">
              <legend className="px-1 text-sm font-semibold text-ink">Botão de ação</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Texto do botão" hint="Vazio = e-mail sem botão.">
                  {({ id, describedBy }) => <Input id={id} aria-describedby={describedBy} value={estado.emailCta} onChange={(e) => patch({ emailCta: e.target.value })} placeholder="Finalizar minha compra" />}
                </Field>
                <Field label="Link do botão" hint="Use {link} para o checkout do próprio lead." error={erroDe('emailUrl')}>
                  {({ id, describedBy, invalid }) => (
                    <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} value={estado.emailUrl} onChange={(e) => patch({ emailUrl: e.target.value })} placeholder="{link}" className="font-mono" />
                  )}
                </Field>
              </div>
            </fieldset>
          ) : null}

          {estado.channel === 'whatsapp_oficial' ? (
            <Checkbox
              checked={estado.sendToMeta}
              onChange={(v) => patch({ sendToMeta: v })}
              label="Enviar para aprovação da Meta ao salvar"
              description="Utility (transacional) costuma ser aprovado em até 24 h; marketing passa por análise mais rígida e exige opt-in. Fora da janela de 24 h após a última resposta do lead, a API oficial só entrega modelo aprovado — por isso a régua usa utility."
            />
          ) : null}

          {estado.channel === 'whatsapp_evolution' ? (
            <Panel tone="accent" className="flex items-start gap-3 px-4 py-3">
              <Icon name="info" size="sm" className="mt-0.5 shrink-0 text-accent" />
              <p className="text-xs text-ink-muted">
                <span className="font-medium text-ink">Texto livre pela Evolution:</span> sem botões e sem aprovação da Meta. Coloque o link no próprio texto com{' '}
                <span className="font-mono text-brand">{'{link}'}</span>. É o número do comercial — use para as respostas da vendedora e o suporte, não para volume.
              </p>
            </Panel>
          ) : null}
        </form>

        <aside aria-label="Prévia, checagem e comparação" className="flex flex-col gap-3 lg:sticky lg:top-0 lg:self-start">
          <Tabs label="Painel lateral do editor" items={abas} value={aba} onChange={setAba} size="sm" panelId="editor-lateral" />
          <div id="editor-lateral" role="tabpanel" className="flex flex-col gap-3">
            {aba === 'previa' ? (
              <>
                <div className="flex flex-wrap gap-1.5">
                  <Badge tone={meta ? 'warning' : 'info'} dot>
                    {meta ? (estado.sendToMeta ? 'Vai para a análise da Meta' : 'Fica pendente, sem enviar') : 'Vai nascer como texto livre'}
                  </Badge>
                  <Badge tone="neutral">{previa.variables.length === 1 ? '1 variável' : `${previa.variables.length} variáveis`}</Badge>
                  <Badge tone="neutral" icon={estado.audience === 'plano_vencido' ? 'calendario' : 'carrinho'}>
                    {PUBLICO_LABEL[estado.audience]}
                    {estado.touch === VALOR_AVULSO ? '' : ` · ${estado.touch}`}
                  </Badge>
                </div>
                <PreviaDoCanal template={previa} empresa={empresa} compacto />
                <DestinoDosBotoes channel={previa.channel} buttons={previa.buttons} audience={previa.audience} />
                <p className="text-xs text-ink-subtle">
                  Remetente: {empresa === null ? 'Converte Fácil (global)' : empresa.name}. As variáveis em vermelho são substituídas no envio; o que ficar entre chaves sem estar na lista
                  sai como texto.
                </p>
              </>
            ) : null}
            {aba === 'checagem' ? <ChecagemMeta regras={regras} passaPelaMeta={meta} /> : null}
            {aba === 'antes_depois' && base !== null ? (
              <>
                <p className="text-sm text-ink-muted">
                  {mudancas.length === 0 ? 'Nada mudou ainda em relação à versão salva.' : `Muda: ${frasearMudancas(mudancas).toLowerCase()}.`}
                  {mudancas.length > 0 && meta && base.status === 'aprovado' ? ' Ao salvar, volta para a análise da Meta.' : ''}
                </p>
                <div className="flex flex-col gap-1.5">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Antes · v{base.versions[base.versions.length - 1]?.version ?? 1} salva</h4>
                  <PreviaDoCanal template={base} empresa={companies.find((c) => c.id === base.companyId) ?? null} compacto />
                </div>
                <div className="flex flex-col gap-1.5">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Depois · o que você está escrevendo</h4>
                  <PreviaDoCanal template={previa} empresa={empresa} compacto />
                </div>
              </>
            ) : null}
          </div>
        </aside>
      </div>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// Pecas
// -----------------------------------------------------------------------------

/**
 * "123/1024" que fica vermelho quando estoura. Cor nunca sozinha: o numero e o texto.
 * So o estouro e anunciado ao leitor de tela: anunciar o contador inteiro leria
 * um numero a cada tecla digitada.
 */
function Contador({ atual, limite, texto }: { readonly atual: number; readonly limite: number; readonly texto?: string }): JSX.Element {
  const estourou = atual > limite;
  return (
    <span className="flex flex-wrap items-center gap-2">
      {texto === undefined ? null : <span>{texto}</span>}
      <span className={`tabular-nums ${estourou ? 'font-medium text-status-critical' : ''}`}>
        {atual}/{limite}
        <span aria-live="polite">{estourou ? ` — acima do limite de ${limite}` : ''}</span>
      </span>
    </span>
  );
}
